import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useRef, useState } from "react";
import { z } from "zod";

import { AccordionButton } from "@/components/Button/AccordionButton";
import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import { HStack, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Confetti, type ConfettiRef } from "@/components/ui/confetti";
import { useAuthStore } from "@/lib/auth";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { ActivateConfirmModal } from "../../-components/ActivateConfirmModal";

const CompletedSchema = z.object({
  addedCatalogID: z.string(),
});

export const Route = createFileRoute(
  "/_staff/device-catalog/add/$deviceType/completed",
)({
  validateSearch: zodValidator(CompletedSchema),
  loaderDeps: ({ search }) => ({ search }),
  component: () => <Completed />,
});

function Completed() {
  const confettiRef = useRef<ConfettiRef>(null);
  const { session } = useAuthStore();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.deviceCatalog.add.form.navigation.completed",
  });
  const { search } = Route.useLoaderDeps();
  const navigate = useNavigate();

  // モーダル状態とMutation
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);

  const handleNavigateToCatalog = useCallback(
    (catalogId: string) => {
      navigate({
        to: "/device-catalog/active/$id/profile",
        params: { id: catalogId },
      });
    },
    [navigate],
  );

  const handleActivateClick = useCallback(() => {
    setIsActivateModalOpen(true);
  }, []);

  return (
    <VStack className="items-center justify-center gap-6">
      <Confetti ref={confettiRef} />

      {/* Result Card */}
      <AttributeCard
        title={t("result")}
        className="w-2/4 p-4"
        actionButton={
          <ActionButton
            variant="secondary"
            onClick={() => {
              navigate({
                to: "/device-catalog/draft/$id/profile",
                params: { id: search.addedCatalogID },
              });
            }}
          >
            {t("goToProfile")}
          </ActionButton>
        }
      >
        <VStack>
          <Text variant="h3">{t("message")}</Text>
          <Spacer />
          <HStack className="items-center">
            <Icon iconName="SquareCheckBig" text={t("completedBy")} />
            <Spacer />
            <Avatar className="grid size-8 place-items-center">
              <AvatarImage src={session?.person.avatarUrl} />
              <AvatarFallback>
                <Icon iconName="UserRoundIcon" />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            {session?.person?.name}
          </HStack>
        </VStack>
      </AttributeCard>

      {/* Continue and Activate Section */}
      <div className="w-2/4 border-b border-neutral-outline">
        <AccordionButton label={t("continueAndActivate")} className="w-full">
          <ActionButton
            size="sm"
            variant="default"
            onClick={handleActivateClick}
            className="w-fit"
          >
            {t("activate")}
          </ActionButton>
        </AccordionButton>
      </div>

      {/* 確認モーダル */}
      <ActivateConfirmModal
        open={isActivateModalOpen}
        onOpenChange={setIsActivateModalOpen}
        catalogId={search.addedCatalogID}
        onSuccess={handleNavigateToCatalog}
      />
    </VStack>
  );
}
